/**
 * ป้าย Kanban การผลิต A6 แนวนอน (148.5×105 มม.) — เรนเดอร์จาก `print_data` อย่างเดียว
 * ไม่คำนวณใหม่จากที่อื่น (D-170 ข้อ 3: สิ่งที่พิมพ์จริงต้องพิมพ์ซ้ำได้เหมือนเดิมแม้
 * ข้อมูลหลักจะเปลี่ยนไปแล้ว) โครง layout แยกไว้ในคอมโพเนนต์เดียวเพราะผู้ใช้จะปรับตาม
 * ภาพร่างจริงทีหลัง (D-170)
 *
 * **หนึ่งป้ายต่อหนึ่งแพ็คเกจ (D-170 ข้อ 4)**: `tag` รับได้ทั้งป้ายเดี่ยว (พรีวิว/พิมพ์
 * ป้ายเดียว) และรายการป้าย (ปุ่ม "พิมพ์ทั้งหมดของ operation") — แต่ละป้ายเรนเดอร์เป็น
 * หน้า A6 ของตัวเอง (`page-break-after` ด้วย inline style เพราะ `desktop.css` อยู่นอก
 * write_scope ของ WO นี้) ปุ่ม "พิมพ์" อยู่นอก loop จึงเรียก `window.print()`ครั้งเดียว
 * ไม่ว่าจะมีกี่หน้า
 *
 * **พื้นที่รูปสินค้า**: `print_data.image_id` มีอยู่ แต่ endpoint รูปของสินค้า
 * (`lib/products/client.ts`) ต้องใช้ `product_id` คู่กับ `image_id` เสมอ และ
 * `product_id` ไม่ได้อยู่ใน `print_data` เลย — จึงเว้นกรอบเปล่าไว้ตามที่ WO อนุญาต
 * แทนการเดา endpoint และรายงานเป็นงานถัดไปใน handoff
 *
 * ใช้ `@media print`/`@page` ใน `desktop.css` เพื่อซ่อนทุกอย่างนอกจากป้ายตอนพิมพ์จริง
 */

import { useEffect, useState } from 'react';
import { toString as qrCodeToSvgString } from 'qrcode';

import { orDash } from '../products/productView';
import { tagColorCss, tagColorTextCss } from '../../lib/outputTags/outputTagView';
import type { OutputTag } from '../../lib/outputTags/types';

export interface KanbanTagSheetProps {
  readonly tag: OutputTag | readonly OutputTag[];
  readonly onClose?: () => void;
}

export function KanbanTagSheet({ tag, onClose }: KanbanTagSheetProps) {
  const tags = Array.isArray(tag) ? tag : [tag];

  return (
    <div className="cap-kanban-tag-wrap" data-testid="output-tag-preview">
      <div className="cap-kanban-tag-controls">
        <button
          type="button"
          className="cap-button cap-button--primary"
          data-testid="output-tag-print-button"
          onClick={() => {
            window.print();
          }}
        >
          พิมพ์
        </button>
        {onClose ? (
          <button
            type="button"
            className="cap-button cap-button--ghost"
            data-testid="output-tag-preview-close"
            onClick={onClose}
          >
            ปิดตัวอย่าง
          </button>
        ) : null}
      </div>

      {tags.map((oneTag, index) => (
        <KanbanTagSheetPage key={oneTag.tag_code} tag={oneTag} pageBreakAfter={index < tags.length - 1} />
      ))}
    </div>
  );
}

function KanbanTagSheetPage({
  tag,
  pageBreakAfter,
}: {
  readonly tag: OutputTag;
  readonly pageBreakAfter: boolean;
}) {
  const data = tag.print_data;
  const [qrSvg, setQrSvg] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setQrSvg(null);
    if (data.qr === null || data.qr.trim() === '') {
      return;
    }
    void qrCodeToSvgString(data.qr, { type: 'svg', margin: 0 }).then((svg) => {
      if (active) {
        setQrSvg(svg);
      }
    });
    return () => {
      active = false;
    };
  }, [data.qr]);

  return (
    <div
      className="cap-kanban-tag"
      data-testid="output-tag-sheet"
      style={pageBreakAfter ? { breakAfter: 'page' } : undefined}
    >
      <div className="cap-kanban-tag__top">
        <div className="cap-kanban-tag__running-no" data-testid="output-tag-running-no">
          {orDash(data.tag_code)}
        </div>
        <div className="cap-kanban-tag__package" data-testid="output-tag-package">
          {data.package_seq !== null && data.package_count !== null
            ? `แพ็คเกจ ${data.package_seq}/${data.package_count}`
            : '—'}
        </div>
        <div
          className="cap-kanban-tag__color"
          data-testid="output-tag-color"
          style={{
            backgroundColor: tagColorCss(data.tag_color),
            color: tagColorTextCss(data.tag_color),
          }}
        >
          {orDash(data.tag_color)}
        </div>
      </div>

      <div className="cap-kanban-tag__body">
        <div className="cap-kanban-tag__main">
          <div className="cap-kanban-tag__part-no" data-testid="output-tag-part-no">
            {orDash(data.part_no)}
          </div>
          <div className="cap-kanban-tag__field" data-testid="output-tag-part-name">
            <span className="cap-kanban-tag__field-label">ชื่อสินค้า</span>
            <span className="cap-kanban-tag__field-value">{orDash(data.part_name)}</span>
          </div>
          <div className="cap-kanban-tag__field" data-testid="output-tag-model">
            <span className="cap-kanban-tag__field-label">Model</span>
            <span className="cap-kanban-tag__field-value">{orDash(data.model)}</span>
          </div>
          <div className="cap-kanban-tag__field" data-testid="output-tag-planning-code">
            <span className="cap-kanban-tag__field-label">Planning Code</span>
            <span className="cap-kanban-tag__field-value">{orDash(data.planning_code)}</span>
          </div>
          <div className="cap-kanban-tag__field" data-testid="output-tag-lot-no">
            <span className="cap-kanban-tag__field-label">Lot No.</span>
            <span className="cap-kanban-tag__field-value">{orDash(data.lot_no)}</span>
          </div>
          <div className="cap-kanban-tag__field" data-testid="output-tag-line">
            <span className="cap-kanban-tag__field-label">Line</span>
            <span className="cap-kanban-tag__field-value">{orDash(data.line_name ?? data.line)}</span>
          </div>
          <div className="cap-kanban-tag__field" data-testid="output-tag-qty-per-pkg">
            <span className="cap-kanban-tag__field-label">Q&apos;ty/Pkg</span>
            <span className="cap-kanban-tag__field-value">
              {orDash(data.tag_qty)} {orDash(data.qty_uom)}
            </span>
          </div>
          <div className="cap-kanban-tag__field" data-testid="output-tag-pkg-type">
            <span className="cap-kanban-tag__field-label">Pkg Type</span>
            <span className="cap-kanban-tag__field-value">{orDash(data.pkg_type)}</span>
          </div>
          <div className="cap-kanban-tag__field" data-testid="output-tag-store-location">
            <span className="cap-kanban-tag__field-label">Store Location</span>
            <span className="cap-kanban-tag__field-value">{orDash(data.store_location)}</span>
          </div>
          <div className="cap-kanban-tag__field" data-testid="output-tag-process-leadtime">
            <span className="cap-kanban-tag__field-label">Process Leadtime</span>
            <span className="cap-kanban-tag__field-value">
              {orDash(data.process_leadtime)} {orDash(data.process_leadtime_basis)}
            </span>
          </div>
        </div>

        <div className="cap-kanban-tag__side">
          <div className="cap-kanban-tag__qr" data-testid="output-tag-qr">
            {qrSvg !== null ? (
              // SVG มาจาก `qrcode` ที่เราเรียกเองด้วยข้อความของ print_data ใบนี้ — ไม่ใช่ข้อความจากผู้ใช้
              <div className="cap-kanban-tag__qr-image" dangerouslySetInnerHTML={{ __html: qrSvg }} />
            ) : (
              <span className="cap-kanban-tag__qr-placeholder">—</span>
            )}
          </div>
          <div className="cap-kanban-tag__image" data-testid="output-tag-image-placeholder">
            {data.image_id === null ? 'ไม่มีรูปสินค้า' : 'รูปสินค้า (ยังไม่แสดง — งานถัดไป)'}
          </div>
        </div>
      </div>

      <div className="cap-kanban-tag__process-flow" data-testid="output-tag-process-flow">
        {data.process_flow.map((step, index) => (
          <span
            key={step.operation_seq}
            className={`cap-kanban-tag__step${step.current ? ' cap-kanban-tag__step--current' : ''}`}
            data-testid={`output-tag-process-step-${step.operation_seq}`}
          >
            {index > 0 ? (
              <span className="cap-kanban-tag__step-arrow" aria-hidden="true">
                →
              </span>
            ) : null}
            {orDash(step.work_center_name ?? step.work_center_code)}
          </span>
        ))}
      </div>
    </div>
  );
}
